import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CrossIcon } from '@THC/components/Icons/Cross';

import styles from './Modal.module.scss';

interface ModalProps {
	children: React.ReactNode | Function;
	contentProps?: React.HTMLAttributes<HTMLDivElement>;
	isOpen: boolean;
	onClose?: () => void;
}

export const Modal: React.FC<ModalProps> = ({ children, contentProps, isOpen, onClose }) => {
	const modalRef = useRef<Nullable<HTMLDialogElement>>(null);
	const [isModalOpen, setIsModalOpen] = useState(isOpen);

	const handleCloseModal = useCallback(() => {
		setIsModalOpen(false);
		onClose?.();
	}, [onClose]);

	useEffect(() => {
		const modal = modalRef.current;
		modal?.addEventListener('cancel', handleCloseModal);
		return () => {
			modal?.removeEventListener('cancel', handleCloseModal);
		};
	}, [handleCloseModal]);

	useEffect(() => {
		setIsModalOpen(isOpen);
	}, [isOpen]);

	useEffect(() => {
		const modalElement = modalRef.current;

		if (!modalElement) return;

		if (isModalOpen) {
			// this is a scroll fix after the modal open/close
			// save scroll position
			document.body.style.top = `-${window.scrollY}px`;
			document.body.classList.add('overflow-hidden');
			modalElement?.showModal?.();
		} else {
			// restore scroll position after modal close
			const scrollY = document.body.style.top;
			document.body?.classList.remove('overflow-hidden');
			modalElement?.close?.();
			document.body.style.top = '';
			window.scrollTo({ top: parseInt(scrollY || '0') * -1 });
		}
	}, [isModalOpen]);

	useEffect(() => {
		return () => {
			document.body.classList.remove('overflow-hidden');
		};
	}, []);

	return (
		<dialog ref={modalRef} className={styles.modal}>
			<div className={styles.content} {...contentProps}>
				{typeof children === 'function' ? children(isModalOpen) : children}
			</div>
			<button
				aria-label="Close modal"
				className={styles.closeButton}
				data-testid="modal-close-button"
				onClick={handleCloseModal}
			>
				<span className={styles.closeIcon}>
					<CrossIcon size="small" />
				</span>
			</button>
		</dialog>
	);
};
